import type { Edge, Node } from '../models/scene';

export type AnchorSide = 'top' | 'right' | 'bottom' | 'left';
export type Direction = AnchorSide;

export interface SpatialRect {
  x: number;
  y: number;
  width: number;
  height: number;
}

export interface SpatialPoint {
  x: number;
  y: number;
}

export interface SmartOrthogonalRouteInput {
  sourceRect: SpatialRect;
  targetRect: SpatialRect;
  sourceAnchor?: AnchorSide;
  targetAnchor?: AnchorSide;
  sourcePoint?: SpatialPoint;
  targetPoint?: SpatialPoint;
  preferredSourceAnchors?: AnchorSide[];
  preferredTargetAnchors?: AnchorSide[];
  obstacles?: SpatialRect[];
  gap?: number;
}

export interface SmartOrthogonalRoute {
  sourceAnchor: AnchorSide;
  targetAnchor: AnchorSide;
  points: number[];
}

export interface ClearBusLaneYInput {
  parentBottomY: number;
  childRects: readonly Pick<SpatialRect, 'y'>[];
  preferredGap?: number;
  clearance?: number;
  minimumGap?: number;
}

export type OrgBusChildRect = Pick<SpatialRect, 'x' | 'y' | 'width' | 'height'>;

const orgBusRectCenter = (rect: OrgBusChildRect) => ({
  x: rect.x + rect.width / 2,
  y: rect.y + rect.height / 2,
});

export const orgRectsSpreadHorizontally = (rects: readonly OrgBusChildRect[]): boolean => {
  if (rects.length <= 1) return false;
  const centers = rects.map(orgBusRectCenter);
  const spreadX = Math.max(...centers.map((center) => center.x)) -
    Math.min(...centers.map((center) => center.x));
  const spreadY = Math.max(...centers.map((center) => center.y)) -
    Math.min(...centers.map((center) => center.y));
  return spreadX >= spreadY;
};

export const orgRectsShareHorizontalRow = (
  subject: OrgBusChildRect,
  peers: readonly OrgBusChildRect[],
): OrgBusChildRect[] => {
  const rowTolerance = Math.max(subject.height * 0.6, 24);
  const subjectCenterY = subject.y + subject.height / 2;
  return peers.filter((rect) => {
    const peerCenterY = rect.y + rect.height / 2;
    return Math.abs(peerCenterY - subjectCenterY) <= rowTolerance;
  });
};

export const orgRectsShareVerticalColumn = (
  subject: OrgBusChildRect,
  peers: readonly OrgBusChildRect[],
): OrgBusChildRect[] => {
  const columnTolerance = Math.max(subject.width * 0.25, 12);
  const subjectCenterX = subject.x + subject.width / 2;
  return peers.filter((rect) => {
    const peerCenterX = rect.x + rect.width / 2;
    return Math.abs(peerCenterX - subjectCenterX) <= columnTolerance;
  });
};

export const orgChildInLeafStackColumn = (
  subject: OrgBusChildRect,
  peers: readonly OrgBusChildRect[],
  isBranch: boolean,
): boolean => {
  if (isBranch || peers.length <= 1) return false;
  const columnPeers = orgRectsShareVerticalColumn(subject, peers);
  if (columnPeers.length <= 1) return false;
  const spreadX = Math.max(...columnPeers.map((rect) => orgBusRectCenter(rect).x)) -
    Math.min(...columnPeers.map((rect) => orgBusRectCenter(rect).x));
  const spreadY = Math.max(...columnPeers.map((rect) => orgBusRectCenter(rect).y)) -
    Math.min(...columnPeers.map((rect) => orgBusRectCenter(rect).y));
  return spreadY > spreadX;
};

export const orgHorizontalBranchRowRectsForBusY = (
  peers: readonly OrgBusChildRect[],
  isBranchByRect: (rect: OrgBusChildRect) => boolean,
): OrgBusChildRect[] => {
  const branchRects = peers.filter(isBranchByRect);
  if (branchRects.length === 0) return [];

  const rowRects = new Map<string, OrgBusChildRect>();
  branchRects.forEach((branchRect) => {
    const rowPeers = orgRectsShareHorizontalRow(branchRect, branchRects);
    if (rowPeers.length <= 1 || !orgRectsSpreadHorizontally(rowPeers)) return;
    rowPeers.forEach((rect) => {
      rowRects.set(`${Math.round(rect.x)}:${Math.round(rect.y)}`, rect);
    });
  });

  return [...rowRects.values()];
};

export interface QuickCreatePlacementInput {
  sourceNodeId: string;
  nodes: readonly Node[];
  edges: readonly Edge[];
  requestedDirection: Direction;
  preferContinuationDirection?: boolean;
  newNodeSize: { width: number; height: number };
  sourceFallbackRect?: SpatialRect;
  gap?: number;
}

export interface QuickCreatePlacementPlan {
  direction: Direction;
  position: SpatialPoint;
  fromAnchor: AnchorSide;
  toAnchor: AnchorSide;
  moves: Array<{
    nodeId: string;
    oldPosition: SpatialPoint;
    newPosition: SpatialPoint;
  }>;
}

const DEFAULT_GAP = 40;
const EPSILON = 0.001;

const positiveNumber = (...values: unknown[]): number | undefined => {
  for (const value of values) {
    if (typeof value === 'number' && Number.isFinite(value) && value > 0) {
      return value;
    }
  }
  return undefined;
};

const finiteNumber = (value: unknown, fallback: number): number => (
  typeof value === 'number' && Number.isFinite(value) ? value : fallback
);

type NodeWithFlexibleGeometry = Node & {
  rectX?: unknown;
  rectY?: unknown;
  rectW?: unknown;
  rectH?: unknown;
  attrs?: Record<string, unknown>;
};

export const rectFromNode = (node: Node): SpatialRect => {
  const flexibleNode = node as NodeWithFlexibleGeometry;
  const attrs = flexibleNode.attrs ?? {};
  const width = positiveNumber(
    flexibleNode.rectW,
    node.width,
    node.w,
    attrs.rectW,
    attrs.width,
    attrs.w,
  ) ?? 120;
  const height = positiveNumber(
    flexibleNode.rectH,
    node.height,
    node.h,
    attrs.rectH,
    attrs.height,
    attrs.h,
  ) ?? 80;
  const usesRectOrigin = positiveNumber(flexibleNode.rectW, attrs.rectW) !== undefined;
  return {
    x: usesRectOrigin ? finiteNumber(flexibleNode.rectX ?? attrs.rectX, node.x ?? 0) : finiteNumber(node.x, 0),
    y: usesRectOrigin ? finiteNumber(flexibleNode.rectY ?? attrs.rectY, node.y ?? 0) : finiteNumber(node.y, 0),
    width,
    height,
  };
};

export const centerOfRect = (rect: SpatialRect): SpatialPoint => ({
  x: rect.x + rect.width / 2,
  y: rect.y + rect.height / 2,
});

export const oppositeAnchor = (side: AnchorSide): AnchorSide => {
  switch (side) {
    case 'top': return 'bottom';
    case 'right': return 'left';
    case 'bottom': return 'top';
    case 'left': return 'right';
  }
};

export const anchorPairForDirection = (
  direction: Direction,
): { fromAnchor: AnchorSide; toAnchor: AnchorSide } => {
  switch (direction) {
    case 'top': return { fromAnchor: 'top', toAnchor: 'bottom' };
    case 'right': return { fromAnchor: 'right', toAnchor: 'left' };
    case 'bottom': return { fromAnchor: 'bottom', toAnchor: 'top' };
    case 'left': return { fromAnchor: 'left', toAnchor: 'right' };
  }
};

export const directionFromDelta = (dx: number, dy: number): Direction => {
  if (Math.abs(dx) >= Math.abs(dy)) return dx >= 0 ? 'right' : 'left';
  return dy >= 0 ? 'bottom' : 'top';
};

const sidePoint = (rect: SpatialRect, side: AnchorSide): SpatialPoint => {
  switch (side) {
    case 'top': return { x: rect.x + rect.width / 2, y: rect.y };
    case 'right': return { x: rect.x + rect.width, y: rect.y + rect.height / 2 };
    case 'bottom': return { x: rect.x + rect.width / 2, y: rect.y + rect.height };
    case 'left': return { x: rect.x, y: rect.y + rect.height / 2 };
  }
};

const sideVector = (side: AnchorSide, outward: boolean): SpatialPoint => {
  const sign = outward ? 1 : -1;
  switch (side) {
    case 'top': return { x: 0, y: -1 * sign };
    case 'right': return { x: 1 * sign, y: 0 };
    case 'bottom': return { x: 0, y: 1 * sign };
    case 'left': return { x: -1 * sign, y: 0 };
  }
};

const axisForSide = (side: AnchorSide): 'horizontal' | 'vertical' => (
  side === 'left' || side === 'right' ? 'horizontal' : 'vertical'
);

const simplifyPoints = (points: number[], eps = EPSILON): number[] => {
  const deduped: number[] = [];
  for (let index = 0; index < points.length; index += 2) {
    const x = points[index] as number;
    const y = points[index + 1] as number;
    const previousX = deduped[deduped.length - 2];
    const previousY = deduped[deduped.length - 1];
    if (
      typeof previousX === 'number' &&
      typeof previousY === 'number' &&
      Math.abs(previousX - x) <= eps &&
      Math.abs(previousY - y) <= eps
    ) {
      continue;
    }
    deduped.push(x, y);
  }

  let simplified = deduped;
  let changed = true;
  while (changed && simplified.length >= 6) {
    changed = false;
    for (let index = 2; index < simplified.length - 2; index += 2) {
      const px = simplified[index - 2] as number;
      const py = simplified[index - 1] as number;
      const cx = simplified[index] as number;
      const cy = simplified[index + 1] as number;
      const nx = simplified[index + 2] as number;
      const ny = simplified[index + 3] as number;
      const vertical = Math.abs(px - cx) <= eps && Math.abs(cx - nx) <= eps;
      const horizontal = Math.abs(py - cy) <= eps && Math.abs(cy - ny) <= eps;
      if (vertical || horizontal) {
        simplified = simplified.slice(0, index).concat(simplified.slice(index + 2));
        changed = true;
        break;
      }
    }
  }
  return simplified;
};

const rectsOverlap = (a: SpatialRect, b: SpatialRect, padding = 0): boolean => (
  a.x < b.x + b.width + padding &&
  a.x + a.width > b.x - padding &&
  a.y < b.y + b.height + padding &&
  a.y + a.height > b.y - padding
);

const segmentIntersectsRect = (
  a: SpatialPoint,
  b: SpatialPoint,
  rect: SpatialRect,
  padding = 0,
): boolean => {
  const left = rect.x - padding;
  const right = rect.x + rect.width + padding;
  const top = rect.y - padding;
  const bottom = rect.y + rect.height + padding;

  if (Math.abs(a.x - b.x) <= EPSILON) {
    const x = a.x;
    if (x <= left || x >= right) return false;
    const minY = Math.min(a.y, b.y);
    const maxY = Math.max(a.y, b.y);
    return maxY > top && minY < bottom;
  }

  if (Math.abs(a.y - b.y) <= EPSILON) {
    const y = a.y;
    if (y <= top || y >= bottom) return false;
    const minX = Math.min(a.x, b.x);
    const maxX = Math.max(a.x, b.x);
    return maxX > left && minX < right;
  }

  return false;
};

export const routeIntersectsRect = (
  points: readonly number[],
  rect: SpatialRect,
  padding = 0,
): boolean => {
  if (!Array.isArray(points) || points.length < 4) return false;

  for (let index = 0; index + 3 < points.length; index += 2) {
    const a = { x: points[index] as number, y: points[index + 1] as number };
    const b = { x: points[index + 2] as number, y: points[index + 3] as number };
    if (
      Number.isFinite(a.x) &&
      Number.isFinite(a.y) &&
      Number.isFinite(b.x) &&
      Number.isFinite(b.y) &&
      segmentIntersectsRect(a, b, rect, padding)
    ) {
      return true;
    }
  }

  return false;
};

export const routeIntersectsAnyRect = (
  points: readonly number[],
  rects: readonly SpatialRect[],
  padding = 0,
): boolean => rects.some((rect) => routeIntersectsRect(points, rect, padding));

const buildRoutePoints = (
  sourceRect: SpatialRect,
  targetRect: SpatialRect,
  sourceAnchor: AnchorSide,
  targetAnchor: AnchorSide,
  gap: number,
  from = sidePoint(sourceRect, sourceAnchor),
  to = sidePoint(targetRect, targetAnchor),
): number[] => {
  const fromVector = sideVector(sourceAnchor, true);
  const toVector = sideVector(targetAnchor, true);
  const pA = { x: from.x + fromVector.x * gap, y: from.y + fromVector.y * gap };
  const pB = { x: to.x + toVector.x * gap, y: to.y + toVector.y * gap };
  const points = [from.x, from.y, pA.x, pA.y];
  const fromAxis = axisForSide(sourceAnchor);
  const toAxis = axisForSide(targetAnchor);

  if (fromAxis !== toAxis) {
    if (fromAxis === 'horizontal') {
      points.push(pB.x, pA.y);
    } else {
      points.push(pA.x, pB.y);
    }
  } else if (fromAxis === 'horizontal') {
    const midX = (pA.x + pB.x) / 2;
    points.push(midX, pA.y, midX, pB.y);
  } else {
    const midY = (pA.y + pB.y) / 2;
    points.push(pA.x, midY, pB.x, midY);
  }

  points.push(pB.x, pB.y, to.x, to.y);
  return simplifyPoints(points);
};

const buildRoutePointCandidates = (
  sourceRect: SpatialRect,
  targetRect: SpatialRect,
  sourceAnchor: AnchorSide,
  targetAnchor: AnchorSide,
  gap: number,
  obstacles: readonly SpatialRect[],
  from = sidePoint(sourceRect, sourceAnchor),
  to = sidePoint(targetRect, targetAnchor),
): number[][] => {
  const base = buildRoutePoints(sourceRect, targetRect, sourceAnchor, targetAnchor, gap, from, to);
  const fromVector = sideVector(sourceAnchor, true);
  const toVector = sideVector(targetAnchor, true);
  const pA = { x: from.x + fromVector.x * gap, y: from.y + fromVector.y * gap };
  const pB = { x: to.x + toVector.x * gap, y: to.y + toVector.y * gap };
  const detourRects = [sourceRect, targetRect, ...obstacles];
  const minObstacleX = Math.min(...detourRects.map((obstacle) => obstacle.x));
  const maxObstacleX = Math.max(...detourRects.map((obstacle) => obstacle.x + obstacle.width));
  const minObstacleY = Math.min(...detourRects.map((obstacle) => obstacle.y));
  const maxObstacleY = Math.max(...detourRects.map((obstacle) => obstacle.y + obstacle.height));

  const candidates = [base];
  for (const detourY of [minObstacleY - gap, maxObstacleY + gap]) {
    candidates.push(simplifyPoints([
      from.x, from.y,
      pA.x, pA.y,
      pA.x, detourY,
      pB.x, detourY,
      pB.x, pB.y,
      to.x, to.y,
    ]));
  }
  for (const detourX of [minObstacleX - gap, maxObstacleX + gap]) {
    candidates.push(simplifyPoints([
      from.x, from.y,
      pA.x, pA.y,
      detourX, pA.y,
      detourX, pB.y,
      pB.x, pB.y,
      to.x, to.y,
    ]));
  }

  return candidates;
};

const routeLength = (points: number[]): number => {
  let total = 0;
  for (let index = 0; index + 3 < points.length; index += 2) {
    total += Math.hypot(
      (points[index + 2] as number) - (points[index] as number),
      (points[index + 3] as number) - (points[index + 1] as number),
    );
  }
  return total;
};

const bendCount = (points: number[]): number => Math.max(0, points.length / 2 - 2);

export const computeClearBusLaneY = ({
  parentBottomY,
  childRects,
  preferredGap = 20,
  clearance = 8,
  minimumGap = 4,
}: ClearBusLaneYInput): number => {
  const desiredY = parentBottomY + preferredGap;
  const childTops = childRects
    .map((rect) => rect.y)
    .filter((top): top is number => Number.isFinite(top));

  if (childTops.length === 0) return desiredY;

  const nearestChildTop = Math.min(...childTops);
  const highestClearY = nearestChildTop - clearance;
  if (desiredY <= highestClearY) return desiredY;

  const minimumClearY = parentBottomY + minimumGap;
  if (highestClearY >= minimumClearY) return highestClearY;

  if (nearestChildTop > parentBottomY) {
    // Too tight for both the requested clearance and the minimum gap. Bias
    // toward the minimum gap so an arrowhead drawn at the parent edge still has
    // room, while never letting the lane touch the child top.
    const available = nearestChildTop - parentBottomY;
    const ceiling = Math.max(1, available - 2);
    return parentBottomY + Math.min(Math.max(minimumGap, 1), ceiling);
  }

  return highestClearY;
};

export const computeSmartOrthogonalRoute = ({
  sourceRect,
  targetRect,
  sourceAnchor,
  targetAnchor,
  sourcePoint,
  targetPoint,
  preferredSourceAnchors = [],
  preferredTargetAnchors = [],
  obstacles = [],
  gap = 12,
}: SmartOrthogonalRouteInput): SmartOrthogonalRoute => {
  const sourceCenter = centerOfRect(sourceRect);
  const targetCenter = centerOfRect(targetRect);
  const inferredDirection = directionFromDelta(targetCenter.x - sourceCenter.x, targetCenter.y - sourceCenter.y);
  const inferredPair = anchorPairForDirection(inferredDirection);
  const sourceCandidates = sourceAnchor ? [sourceAnchor] : (['top', 'right', 'bottom', 'left'] as AnchorSide[]);
  const targetCandidates = targetAnchor ? [targetAnchor] : (['top', 'right', 'bottom', 'left'] as AnchorSide[]);

  let best: { score: number; route: SmartOrthogonalRoute } | null = null;

  for (const candidateSource of sourceCandidates) {
    for (const candidateTarget of targetCandidates) {
      if (candidateSource === candidateTarget && sourceAnchor == null && targetAnchor == null) {
        continue;
      }
      const routeCandidates = buildRoutePointCandidates(
        sourceRect,
        targetRect,
        candidateSource,
        candidateTarget,
        gap,
        obstacles,
        sourcePoint,
        targetPoint,
      );

      for (const points of routeCandidates) {
        let obstaclePenalty = 0;
        let endpointInteriorPenalty = 0;
        for (let index = 0; index + 3 < points.length; index += 2) {
          const a = { x: points[index] as number, y: points[index + 1] as number };
          const b = { x: points[index + 2] as number, y: points[index + 3] as number };
          for (const obstacle of obstacles) {
            if (segmentIntersectsRect(a, b, obstacle, 2)) obstaclePenalty += 1;
          }
          if (segmentIntersectsRect(a, b, sourceRect)) endpointInteriorPenalty += 1;
          if (segmentIntersectsRect(a, b, targetRect)) endpointInteriorPenalty += 1;
        }

        const preferencePenalty =
          (preferredSourceAnchors.length > 0 && !preferredSourceAnchors.includes(candidateSource) ? 120 : 0) +
          (preferredTargetAnchors.length > 0 && !preferredTargetAnchors.includes(candidateTarget) ? 120 : 0);
        const inferredPenalty =
          (candidateSource === inferredPair.fromAnchor ? 0 : 16) +
          (candidateTarget === inferredPair.toAnchor ? 0 : 16);
        const score =
          obstaclePenalty * 10000 +
          endpointInteriorPenalty * 10000 +
          bendCount(points) * 1000 +
          preferencePenalty +
          inferredPenalty +
          routeLength(points);

        if (!best || score < best.score) {
          best = {
            score,
            route: {
              sourceAnchor: candidateSource,
              targetAnchor: candidateTarget,
              points,
            },
          };
        }
      }
    }
  }

  return best?.route ?? {
    sourceAnchor: inferredPair.fromAnchor,
    targetAnchor: inferredPair.toAnchor,
    points: buildRoutePoints(sourceRect, targetRect, inferredPair.fromAnchor, inferredPair.toAnchor, gap),
  };
};

/** Repair endpoint crossings without considering unrelated canvas shapes. */
export const repairOrthogonalEndpointIntersections = (
  points: number[],
  endpoints: Required<Pick<SmartOrthogonalRouteInput,
    'sourceRect' | 'targetRect' | 'sourceAnchor' | 'targetAnchor' | 'sourcePoint' | 'targetPoint'>>,
): number[] => {
  if (!routeIntersectsAnyRect(points, [endpoints.sourceRect, endpoints.targetRect])) return points;
  return computeSmartOrthogonalRoute(endpoints).points;
};

const isReportingEdge = (edge: Edge): boolean => {
  const edgeType = String(edge.type ?? '');
  const relation = String((edge.attrs as Record<string, unknown> | undefined)?.relation ?? '');
  return (
    edgeType === 'reports_to' ||
    edgeType === 'reporting' ||
    edgeType === 'scenario_reporting' ||
    relation === 'reports_to'
  );
};

export const inferContinuationDirection = ({
  sourceNodeId,
  nodes,
  edges,
  fallbackDirection,
}: {
  sourceNodeId: string;
  nodes: readonly Node[];
  edges: readonly Edge[];
  fallbackDirection: Direction;
}): Direction => {
  const nodeById = new Map(nodes.map((node) => [node.id, node]));
  const sourceNode = nodeById.get(sourceNodeId);
  if (!sourceNode) return fallbackDirection;
  const sourceCenter = centerOfRect(rectFromNode(sourceNode));
  const samples: Array<{ direction: Direction; distance: number; requestedMatch: boolean }> = [];

  for (const edge of edges) {
    const fromId = edge.from as string | undefined;
    const toId = edge.to as string | undefined;
    if (!fromId || !toId) continue;
    let vector: { dx: number; dy: number } | null = null;

    if (fromId === sourceNodeId) {
      const other = nodeById.get(toId);
      if (!other) continue;
      const otherCenter = centerOfRect(rectFromNode(other));
      vector = { dx: otherCenter.x - sourceCenter.x, dy: otherCenter.y - sourceCenter.y };
    } else if (toId === sourceNodeId) {
      const other = nodeById.get(fromId);
      if (!other) continue;
      const otherCenter = centerOfRect(rectFromNode(other));
      vector = isReportingEdge(edge)
        ? { dx: otherCenter.x - sourceCenter.x, dy: otherCenter.y - sourceCenter.y }
        : { dx: sourceCenter.x - otherCenter.x, dy: sourceCenter.y - otherCenter.y };
    }

    if (!vector) continue;
    const distance = Math.hypot(vector.dx, vector.dy);
    if (distance <= 1) continue;
    const direction = directionFromDelta(vector.dx, vector.dy);
    samples.push({ direction, distance, requestedMatch: direction === fallbackDirection });
  }

  if (samples.length === 0) return fallbackDirection;
  samples.sort((left, right) => {
    if (left.requestedMatch !== right.requestedMatch) return left.requestedMatch ? -1 : 1;
    return right.distance - left.distance;
  });
  return samples[0]?.direction ?? fallbackDirection;
};

const laneOverlaps = (rect: SpatialRect, lane: SpatialRect, direction: Direction): boolean => {
  if (direction === 'right' || direction === 'left') {
    return rect.y < lane.y + lane.height && rect.y + rect.height > lane.y;
  }
  return rect.x < lane.x + lane.width && rect.x + rect.width > lane.x;
};

const isBeyondInsertion = (
  rect: SpatialRect,
  sourceRect: SpatialRect,
  insertionRect: SpatialRect,
  direction: Direction,
): boolean => {
  const rectCenter = centerOfRect(rect);
  const sourceCenter = centerOfRect(sourceRect);
  switch (direction) {
    case 'right': return rectCenter.x > sourceCenter.x && rect.x >= insertionRect.x - 1;
    case 'left': return rectCenter.x < sourceCenter.x && rect.x + rect.width <= insertionRect.x + insertionRect.width + 1;
    case 'bottom': return rectCenter.y > sourceCenter.y && rect.y >= insertionRect.y - 1;
    case 'top': return rectCenter.y < sourceCenter.y && rect.y + rect.height <= insertionRect.y + insertionRect.height + 1;
  }
};

export const planQuickCreatePlacement = ({
  sourceNodeId,
  nodes,
  edges,
  requestedDirection,
  preferContinuationDirection = false,
  newNodeSize,
  sourceFallbackRect,
  gap = DEFAULT_GAP,
}: QuickCreatePlacementInput): QuickCreatePlacementPlan => {
  const sourceNode = nodes.find((node) => node.id === sourceNodeId);
  const sourceRect = sourceNode ? rectFromNode(sourceNode) : sourceFallbackRect;
  if (!sourceRect) {
    const { fromAnchor, toAnchor } = anchorPairForDirection(requestedDirection);
    return {
      direction: requestedDirection,
      position: { x: 0, y: 0 },
      fromAnchor,
      toAnchor,
      moves: [],
    };
  }

  const direction = sourceNode && preferContinuationDirection
    ? inferContinuationDirection({ sourceNodeId, nodes, edges, fallbackDirection: requestedDirection })
    : requestedDirection;
  const { fromAnchor, toAnchor } = anchorPairForDirection(direction);
  const sourceCenter = centerOfRect(sourceRect);
  let position: SpatialPoint;

  switch (direction) {
    case 'right':
      position = {
        x: sourceRect.x + sourceRect.width + gap,
        y: Math.round(sourceCenter.y - newNodeSize.height / 2),
      };
      break;
    case 'left':
      position = {
        x: sourceRect.x - gap - newNodeSize.width,
        y: Math.round(sourceCenter.y - newNodeSize.height / 2),
      };
      break;
    case 'bottom':
      position = {
        x: Math.round(sourceCenter.x - newNodeSize.width / 2),
        y: sourceRect.y + sourceRect.height + gap,
      };
      break;
    case 'top':
      position = {
        x: Math.round(sourceCenter.x - newNodeSize.width / 2),
        y: sourceRect.y - gap - newNodeSize.height,
      };
      break;
  }

  const insertionRect = {
    x: position.x,
    y: position.y,
    width: newNodeSize.width,
    height: newNodeSize.height,
  };
  const occupied = nodes.some((node) => (
    node.id !== sourceNodeId && rectsOverlap(rectFromNode(node), insertionRect, 4)
  ));
  const moves: QuickCreatePlacementPlan['moves'] = [];

  if (occupied) {
    const lane = direction === 'right' || direction === 'left'
      ? {
          x: Math.min(sourceRect.x, insertionRect.x),
          y: Math.min(sourceRect.y, insertionRect.y) - gap / 2,
          width: Math.abs(insertionRect.x - sourceRect.x) + sourceRect.width + insertionRect.width,
          height: Math.max(sourceRect.height, insertionRect.height) + gap,
        }
      : {
          x: Math.min(sourceRect.x, insertionRect.x) - gap / 2,
          y: Math.min(sourceRect.y, insertionRect.y),
          width: Math.max(sourceRect.width, insertionRect.width) + gap,
          height: Math.abs(insertionRect.y - sourceRect.y) + sourceRect.height + insertionRect.height,
        };
    const shift = direction === 'right' || direction === 'left'
      ? newNodeSize.width + gap
      : newNodeSize.height + gap;
    const dx = direction === 'right' ? shift : direction === 'left' ? -shift : 0;
    const dy = direction === 'bottom' ? shift : direction === 'top' ? -shift : 0;

    for (const node of nodes) {
      if (node.id === sourceNodeId) continue;
      const rect = rectFromNode(node);
      if (!laneOverlaps(rect, lane, direction)) continue;
      if (!isBeyondInsertion(rect, sourceRect, insertionRect, direction)) continue;
      moves.push({
        nodeId: node.id,
        oldPosition: { x: finiteNumber(node.x, rect.x), y: finiteNumber(node.y, rect.y) },
        newPosition: { x: finiteNumber(node.x, rect.x) + dx, y: finiteNumber(node.y, rect.y) + dy },
      });
    }
  }

  return {
    direction,
    position,
    fromAnchor,
    toAnchor,
    moves,
  };
};
